import { gql } from '@apollo/client';

export interface Position {
  positionKey: number;
  assetKey: number;
  symbol: string;
  positionType: 'EQUITY' | 'OPTION' | 'CASH' | 'CRYPTO';
  optionType?: 'CALL' | 'PUT';
  strikePrice?: number;
  expiresAt?: string;
}

export interface PositionHistory {
  positionHistoryKey: number;
  portfolioScopeKey: number;
  positionKey: number;
  sequenceNumber: number;
  orderKey?: number;
  totalNetQuantity: number;
  averageUnitCostBasis: number;
  cumulativeRealizedProfitLoss: number;
  unaccountedForShares: number;
  computedAt: string;
  changedAt?: string;
  changeAdjustedAssetPrice: number;
  changeAdjustedQuantity: number;
  position: Position;
}

export interface AutoPilotSettings {
  autoPilotSettingsKey: number;
  masterUser?: {
    firstName: string;
    lastName: string;
  };
  masterPortfolio?: {
    autoPilotMasterPortfolio?: {
      title: string;
    };
  };
}

export interface PortfolioScope {
  portfolioScopeKey: number;
  portfolioKey: number;
  autoPilotSettingsKey?: number;
  type: number;
  autoPilotSettings?: AutoPilotSettings;
  positionHistory: PositionHistory[];
}

export interface PortfolioPositionsData {
  portfolioGet: {
    portfolio: {
      portfolioKey: number;
      nickname: string;
      brokerName: string;
      portfolioScope: PortfolioScope[];
    };
  };
}

// Add PortfolioData interface alias for compatibility
export interface PortfolioData {
  portfolioKey: number;
  nickname: string;
  brokerName: string;
  portfolioScope: PortfolioScope[];
}

// Add PORTFOLIO_SCOPE_TYPE constants
export const PORTFOLIO_SCOPE_TYPE = {
  ENTIRE_ACCOUNT: 9,
  MANUAL: 8,
  PILOTED: 7,
  RISK_STDDEV: 1,
  RISK_DOWNDEV: 2,
  RISK_BETA: 3,
  RISK_SHARPE: 4,
  RISK_SORTINO: 5,
  RISK_ALPHA: 6,
} as const;

// Add getPortfolioScopeTypeName function
export const getPortfolioScopeTypeName = (type: number): string => {
  switch (type) {
    case PORTFOLIO_SCOPE_TYPE.ENTIRE_ACCOUNT:
      return 'BROKERAGE';
    case PORTFOLIO_SCOPE_TYPE.MANUAL:
      return 'MANUAL';
    case PORTFOLIO_SCOPE_TYPE.PILOTED:
      return 'PILOTED';
    case PORTFOLIO_SCOPE_TYPE.RISK_STDDEV:
      return 'RISK_STDDEV';
    case PORTFOLIO_SCOPE_TYPE.RISK_DOWNDEV:
      return 'RISK_DOWNDEV';
    case PORTFOLIO_SCOPE_TYPE.RISK_BETA:
      return 'RISK_BETA';
    case PORTFOLIO_SCOPE_TYPE.RISK_SHARPE:
      return 'RISK_SHARPE';
    case PORTFOLIO_SCOPE_TYPE.RISK_SORTINO:
      return 'RISK_SORTINO';
    case PORTFOLIO_SCOPE_TYPE.RISK_ALPHA:
      return 'RISK_ALPHA';
    default:
      return 'UNKNOWN';
  }
};

export const calculatePositionHistorySequences= gql`
  mutation calculatePositionHistorySequences($portfolioKey: Int!, $positionKey: Int!) {
    jediCalculatePositionHistorySequences(input: { portfolioKey: $portfolioKey, positionKey: $positionKey }) {
      portfolioKey 
    }
  }
`;

export const calculatePortfolioHistorySequences= gql`
  mutation calculatePortfolioHistorySequences($portfolioKey: Int!) {
    jediCalculatePositionHistorySequencesForPortfolio(input: { portfolioKey: $portfolioKey }) {
      portfolioKey
    }
  }
`;

export const jediPortfolioOrdersSync = gql`
  mutation jediPortfolioOrdersSync($portfolioKeys: [Int!]!, $global: Boolean!) {
    jediPortfolioOrdersSync(input: { portfolioKeys: $portfolioKeys, global: $global }) {
      success
      message
    }
  }
`;

export const portfolioPositionsGet = gql`
  query portfolioPositionsGet($portfolioKey: Int!, $types: [PORTFOLIO_SCOPE_TYPE]) {
    portfolioGet(input: { portfolioKey: $portfolioKey }) {
      portfolio {
        portfolioKey
        nickname
        brokerName
        portfolioScope(input: { type: $types }) {
          portfolioScopeKey
          portfolioKey
          autoPilotSettingsKey
          type
          positionHistory(input: { latest: true }) {
            positionHistoryKey
            portfolioScopeKey
            positionKey
            sequenceNumber
            orderKey
            totalNetQuantity
            averageUnitCostBasis
            cumulativeRealizedProfitLoss
            unaccountedForShares
            computedAt
            changedAt
            changeAdjustedAssetPrice
            changeAdjustedQuantity
            position {
              positionKey
              assetKey
              symbol
              positionType
              optionType
              strikePrice
              expiresAt
            }
          }
        }
      }
    }
  }
`;

// Add portfolioScopeGet query alias for compatibility
export const portfolioScopeGet = portfolioPositionsGet;

export const portfolioPositionHistoryGet = gql`
  query portfolioPositionHistoryGet($portfolioKey: Int!, $types: [PORTFOLIO_SCOPE_TYPE], $positionKey: Int) {
    portfolioGet(input: { portfolioKey: $portfolioKey }) {
      portfolio {
        portfolioKey
        nickname
        brokerName
        portfolioScope(input: { type: $types }) {
          portfolioScopeKey
          portfolioKey
          autoPilotSettingsKey
          type
          positionHistory(input: { positionKey: $positionKey }) {
            positionHistoryKey
            portfolioScopeKey
            positionKey
            sequenceNumber
            orderKey
            totalNetQuantity
            averageUnitCostBasis
            cumulativeRealizedProfitLoss
            unaccountedForShares
            computedAt
            changedAt
            changeAdjustedAssetPrice
            changeAdjustedQuantity
            position {
              positionKey
              assetKey
              symbol
              positionType
              optionType
              strikePrice
              expiresAt
            }
          }
        }
      }
    }
  }
`;

export const autoPilotSettingsGet = gql`
  query autoPilotSettingsGet($autoPilotSettingsKey: Int!) {
    whoisit(input: { autoPilotSettingsKey: $autoPilotSettingsKey }) {
      user {
        userKey
        firstName
        lastName
      }
      autoPilotSettings {
        autoPilotSettingsKey
        masterUser {
          firstName
          lastName
        }
        masterPortfolio {
          autoPilotMasterPortfolio {
            title
          }
        }
      }
    }
  }
`;